<template>
  <div class="org-menu-header-side">
    <div style="max-width: calc(100% - 150px)">
      <v-row>
        <v-card-text class="col-12 header-status-icon">
          <div class="header-header-container">
            <span class="header-position-custom-icon">
              <Icon v-if="type === 'Position'" name="org_icon_position" />
              <Icon v-else-if="type === 'Role'" name="org_icon_role" />
              <Icon v-else name="org_icon_responsibility" />
            </span>
            <div class="mt-1" style="width: 100%">
              <div class="lock-status">
                <v-img
                  v-if="lockShow"
                  :src="require('@components/assets/header-lock-logo.svg')"
                  :width="20"
                />
                <StatusBadge
                  :status="status"
                  :archived="archived"
                  :backgroundColor="status === 'archived' ? '#D92D20' : ''"
                />
              </div>
              <div class="header-object-name" style="width: 100%">
                <OrgObjectCard
                  :code="code"
                  :name="name"
                  :removeUnderlineToName="true"
                />
              </div>
            </div>
          </div>
        </v-card-text>
      </v-row>
    </div>
    <div class="header-org-menu header-chatter-with-position">
      <EmployeeReadinessHeaderBox
      v-if="type === 'Responsibility'"
          :readiness="readiness"
          :last_month_readiness="last_month_readiness"
        />
      <div
        @click="openChatter(currentPosition)"
        class="hide-left-border center-vertical header-chatter-type-icon"
        :class="{
          'header-chatter-lock-icon': lockShow,
        }"
      >
        <ChatterIcon :is_white="false" class="header-chatter-icon" />
      </div>
      <div v-if="lockShow" class="header-lock-icon">
        <v-img
          class="header-chatter-icon"
          :src="require('@components/assets/unlock.svg')"
        />
      </div>
      <DropDownMenu
        class="header-org-obj-menu"
        v-else
        :is-open="false"
        :dropdown-items="getOrgObjectDropdownItems(dynamicModalHeaderState)"
        @onClick="
          emit('optionClick', { item: $event, data: dynamicModalHeaderState })
        "
      >
        <template #activator>
          <div class="icon-container">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="icon-svg"
            >
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </template>
      </DropDownMenu>
    </div>
    <ChatterDrawer
      ref="drawerContainer"
      :drawer="chatterDrawerOpen"
      :iconName="drawerHeaderState"
      :type="type"
      :typeValue="name || (currentPosition && currentPosition.name)"
      :listData="listData"
      @closeDrawer="closeDrawer($event)"
      :chatterList="dataStream"
      :totalCheckpointChatter="totalChatterCount"
      @requestMoreCheckpointsChatter="
        emit('requestMoreCheckpointsChatter', $event)
      "
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed } from "vue";
import StatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import Icon from "@components/icons/Icon.vue";
import OrgObjectCard from "@components/common/OrgObjectCard.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import EmployeeReadinessHeaderBox from "@components/EmployeeReadinessHeaderBox/EmployeeReadinessHeaderBox.vue";

const props = defineProps({
  type: {
    type: String,
    default: "Position",
  },
  status: {
    type: String,
    default: "",
  },
  code: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  archived: {
    type: Boolean,
    default: false,
  },
  lockShow: {
    type: Boolean,
    default: false,
  },
  currentPosition: {
    type: Object,
    default() {
      return {};
    },
  },
  modalHeaderState: {
    type: Object,
  },
  dataStream: {
    type: Array,
    default: () => [],
  },
  totalChatterCount: {
    type: Number,
    default: 0,
  },
  readiness: {
    type: Number,
    default: 0,
  },
  last_month_readiness: {
    type: Number,
    default: 0,
  },
  chatterDrawerOpen: {
    type: Boolean,
    default: false,
  },
});

const listData = ref({});
const drawerContainer = ref(null);

const dynamicModalHeaderState = computed(() => {
  if (
    props.modalHeaderState &&
    Object.keys(props.modalHeaderState).length > 0
  ) {
    return props.modalHeaderState;
  } else {
    return {
      status: props.status,
      icon: `org_icon_${props.type.toLowerCase()}`,
      label: props.type,
      code: props.code,
      name: props.name,
      archived: props.archived,
    };
  }
});

const drawerHeaderState = computed(() => {
  if (props.type === "Position") {
    return "org_icon_position";
  } else if (props.type === "Role") {
    return "org_icon_role";
  } else {
    return "org_icon_responsibility";
  }
});

const openChatter = (data) => {
  listData.value = data;
  emit("update:chatterDrawerOpen", true);
  emit("reloadChatterList");
};

const closeDrawer = (data) => {
  if (data) {
    emit("update:chatterDrawerOpen", false);
  }
};

const emit = defineEmits([
  "optionClick",
  "requestMoreCheckpointsChatter",
  "reloadChatterList",
  "update:chatterDrawerOpen",
]);

const getOrgObjectDropdownItems = (data) => {
  if (props.type === "Responsibility" || data.label === "Responsibility") {
    const items = [];
    const status = data.status?.toLowerCase();
    const isArchived = data.archived || false;

    // Draft status: Activate, Attach Role, Rename, Delete
    if (status === "draft") {
      items.push({
        name: "Activate",
        value: "activate_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
      items.push({
        name: "Attach to Role",
        value: "attach_role",
        icon: "icons/dropdown/attach.svg",
        order: 2,
      });
    }

    // Active status: Retire, Attach Role
    if (status === "active") {
      items.push({
        name: "Retire",
        value: "retire_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
      items.push({
        name: "Attach to Role",
        value: "attach_role",
        icon: "icons/dropdown/attach.svg",
        order: 2,
      });
    }

    // Retired (not archived): Re-activate, Archive
    if (status === "retired" && !isArchived) {
      items.push({
        name: "Re-activate",
        value: "reactivate_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
      items.push({
        name: "Archive",
        value: "archive_action",
        icon: "icons/dropdown/archive.svg",
        order: 5,
      });
    }

    // Retired (archived): Unarchive only
    if (status === "archived" && isArchived) {
      items.push({
        name: "Unarchive",
        value: "unarchive_action",
        icon: "icons/dropdown/unarchive.svg",
        order: 5,
      });
    }

    // Inactive status: Re-activate
    if (status === "inactive") {
      items.push({
        name: "Re-activate",
        value: "reactivate_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
    }

    // Rename - Only when not archived
    if (!isArchived) {
      items.push({
        name: "Rename",
        value: "rename_action",
        icon: "icons/dropdown/rename.svg",
        order: 4,
      });
    }

    // Delete - Only when Draft (with negative: true for red color)
    if (status === "draft") {
      items.push({
        name: "Delete",
        value: "delete_action",
        icon: "icons/dropdown/delete.svg",
        negative: true,
        order: 6,
      });
    }

    // Sort by order
    items.sort((a, b) => a.order - b.order);

    return items;
  } else if (props.type === "Role" || data.label === "Role") {
    // Role-specific dropdown items
    let items = [];

    if (!data || !data.hasOwnProperty("status")) {
      return items;
    }

    const isArchived = data.archived || false;

    if (data.status == "draft") {
      items = [
        {
          name: "Activate",
          value: "activate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Attach Responsibility",
          value: "attach_responsibility",
          icon: "icons/dropdown/attach.svg",
          order: 2,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 3,
        },
        {
          name: "Delete",
          value: "delete_action",
          icon: "icons/dropdown/delete.svg",
          negative: true,
          order: 4,
        },
      ];
    } else if (data.status === "active") {
      items = [
        {
          name: "Retire",
          value: "retire_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Attach Responsibility",
          value: "attach_responsibility",
          icon: "icons/dropdown/attach.svg",
          order: 2,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 3,
        },
      ];
    } else if (data.status == "inactive") {
      items = [
        {
          name: "Re-Activate",
          value: "reactivate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 2,
        },
      ];
    } else if (data.status == "retired") {
      if (isArchived) {
        items = [
          {
            name: "Unarchive",
            value: "unarchive_action",
            icon: "icons/dropdown/unarchive.svg",
            order: 1,
          },
        ];
      } else {
        items = [
          {
            name: "Re-Activate",
            value: "reactivate_action",
            icon: "icons/dropdown/activate.svg",
            order: 1,
          },
          {
            name: "Rename",
            value: "rename_action",
            icon: "icons/dropdown/rename.svg",
            order: 2,
          },
          {
            name: "Archive",
            value: "archive_action",
            icon: "icons/dropdown/archive.svg",
            order: 3,
          },
        ];
      }
    } else if (data.status?.toLowerCase() == "archived" && isArchived) {
      items = [
        {
          name: "Unarchive",
          value: "unarchive_action",
          icon: "icons/dropdown/unarchive.svg",
          order: 1,
        },
      ];
    }
    return items;
  } else {
    // Position-specific dropdown items
    let items = [];

    if (!data || !data.hasOwnProperty("status")) {
      return items;
    }

    if (data.status == "draft") {
      items = [
        {
          name: "Activate",
          value: "activate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 2,
        },
        {
          name: "Delete",
          value: "delete_action",
          icon: "icons/dropdown/delete.svg",
          negative: true,
          order: 3,
        },
        {
          name: "Clone",
          value: "cloned_action",
          icon: "icons/dropdown/clone.png",
          order: 4,
        },
      ];
    } else if (data.status === "active") {
      if (data.assigned_employees && data.assigned_employees.length === 0) {
        items = [
          {
            name: "Retire",
            value: "retire_action",
            icon: "icons/dropdown/activate.svg",
            order: 1,
          },
          // hide for ticket ER-3750
          // {
          //   name: 'De-Activate',
          //   value: 'deactivate_action',
          //   icon: 'icons/dropdown/activate.svg',
          //   order: 2
          // },
          ...items,
        ];
      }

      items = [
        ...items,
        {
          name: "Attach Role",
          value: "attach_position_role",
          icon: "icons/dropdown/attach.svg",
          order: 3,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 4,
        },
        {
          name: "Clone",
          value: "cloned_action",
          icon: "icons/dropdown/clone.png",
          order: 5,
        },
      ];
    } else if (data.status == "inactive") {
      items = [
        {
          name: "Re-Activate",
          value: "reactivate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 2,
        },
        {
          name: "Clone",
          value: "cloned_action",
          icon: "icons/dropdown/clone.png",
          order: 3,
        },
      ];
    } else if (data.status == "retired") {
      if (data.archived === true) {
        items = [
          {
            name: "Unarchive",
            value: "unarchive_action",
            icon: "icons/dropdown/unarchive.svg",
            order: 1,
          },
        ];
      } else {
        items = [
          {
            name: "Re-Activate",
            value: "reactivate_action",
            icon: "icons/dropdown/activate.svg",
            order: 1,
          },
          {
            name: "Rename",
            value: "rename_action",
            icon: "icons/dropdown/rename.svg",
            order: 2,
          },
          {
            name: "Clone",
            value: "cloned_action",
            icon: "icons/dropdown/clone.png",
            order: 3,
          },
          {
            name: "Archive",
            value: "archive_action",
            icon: "icons/dropdown/archive.svg",
            order: 4,
          },
        ];
      }
    } else if (data.status?.toLowerCase() == "archived" || data.archived === true) {
      items = [
        {
          name: "Unarchive",
          value: "unarchive_action",
          icon: "icons/dropdown/unarchive.svg",
          order: 1,
        },
      ];
    }
    return items;
  }
};
</script>
<style>
.header-org-menu {
  position: relative;
  right: 25px;
  top: 10px;
}
.header-chatter-with-position {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}
.header-chatter-type-icon {
  display: grid;
  justify-content: end;
}
.header-chatter-type-icon {
  padding: 6px;
  position: relative;
  left: 12px;
}
.org-menu-header-side {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-status-icon {
  margin-left: 20px;
  margin-top: 10px;
}
.header-header-container {
  display: flex;
  column-gap: 10px;
}
.header-object-name .url-name {
  text-decoration: none;
}
.header-object-name .url-name:hover {
  background-color: transparent;
}
.header-position-custom-icon svg {
  width: 50px;
  height: 50px;
}
.header-object-name .org-object-card {
  flex-direction: row-reverse;
  gap: 10px;
  display: inline-flex !important;
}
.header-object-name .org-object-card:hover {
  background: none;
}
.header-object-name {
  margin-top: -8px;
  margin-left: 1px;
}
.header-object-name .org-object-card .el-tooltip._parent-content {
  width: auto !important;
}
.header-org-obj-menu {
  position: relative;
  top: 4px;
}
.header-org-obj-menu .ex-generic-selector-measurement-clone {
  position: fixed !important;
}
.header-object-name .code-text {
  color: #98a2b3;
}
.lock-status {
  display: inline-flex;
  gap: 5px;
}
.header-lock-icon {
  position: relative;
  top: 5px;
  left: 5px;
}
.header-chatter-lock-icon {
  left: 6px !important;
}
.header-org-menu .ex-employee-readiness-box {
  margin-right: 0px !important;
}
</style>
